import type { OverlayFill } from './types';

/**
 * READING AND WRITING `OverlayFill` WITHOUT DESTROYING IT.
 *
 * 🔴 The bug this module exists to end: the inspector's colour row used to call
 * a `resolveFillHex` that flattened `{type:'linear', colors:[a,b], angle}` to
 * `colors[0]`, and the row's `onChange` wrote a bare hex string back. So opening
 * the colour swatch on a gradient text layer and nudging it one step silently
 * replaced a two-stop gradient with a flat fill — the user's second colour and
 * their angle were gone, with no warning and no undo affordance naming it.
 *
 * A lossy READ is only half the problem: the fix is a lossy-free WRITE. Every
 * function here takes the CURRENT fill and returns a fill of the SAME shape,
 * so a gradient stays a gradient unless the caller explicitly flattens it.
 *
 * The union's two gradient members differ only in that one carries an exact
 * two-tuple. Widening a tuple to `string[]` on write would type-check and then
 * silently change which member of the union the value satisfies, so the tuple
 * shape is preserved explicitly.
 */

export const DEFAULT_FILL_HEX = '#ffffff';

export function isGradientFill(fill: OverlayFill): fill is Exclude<OverlayFill, string> {
  return typeof fill !== 'string';
}

/** The stops, in order. A flat fill is one stop — no special-casing at call sites. */
export function fillStops(fill: OverlayFill): string[] {
  if (typeof fill === 'string') return [fill];
  return [...fill.colors];
}

/** The first stop, for anywhere a single swatch has to stand for the fill. */
export function primaryFillHex(fill: OverlayFill): string {
  return fillStops(fill)[0] ?? DEFAULT_FILL_HEX;
}

export function fillAngle(fill: OverlayFill): number {
  if (typeof fill === 'string') return 0;
  return fill.angle ?? 0;
}

export function fillTypeLabel(fill: OverlayFill): string {
  if (typeof fill === 'string') return 'Solid';
  return fill.type === 'radial' ? 'Radial gradient' : 'Linear gradient';
}

/**
 * Replace ONE stop. A flat fill with `index === 0` stays flat; a gradient stays
 * a gradient with every other stop, its type and its angle untouched.
 */
export function withFillStop(fill: OverlayFill, index: number, hex: string): OverlayFill {
  if (typeof fill === 'string') return index === 0 ? hex : fill;
  const next = [...fill.colors];
  if (index < 0 || index >= next.length) return fill;
  next[index] = hex;
  if (next.length === 2) {
    return { type: fill.type, colors: [next[0], next[1]], angle: fill.angle };
  }
  // The 3+-stop member is `linear`-only in the type, so a radial can never
  // reach here; returning `fill` unchanged would be a silent no-op, so assert
  // the only shape the union allows.
  return { type: 'linear', colors: next, angle: fill.angle ?? 0 };
}

export function withFillAngle(fill: OverlayFill, angle: number): OverlayFill {
  if (typeof fill === 'string') return fill;
  const normalised = ((Math.round(angle) % 360) + 360) % 360;
  if (fill.colors.length === 2) {
    return { type: fill.type, colors: [fill.colors[0], fill.colors[1]], angle: normalised };
  }
  return { type: 'linear', colors: [...fill.colors], angle: normalised };
}
